import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Grid, Box, Typography, Stack, Button, Card, CardContent } from '@mui/material';
import {
  AISolutionId,
  EnterpriseApplyPayload,
  IAISolution,
  IAISolutionResponse,
  IAllAgents,
  IEnhancedAISolution,
  IIDataSource,
  IMutationAISolution,
} from '../../common/types/common.ts';
import SectionCard from '../../common/components/SectionCard/sectionCard.tsx';
import Lists from './List.tsx';
import Storage from '@mui/icons-material/Storage';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import Add from '@mui/icons-material/Add';
import TopBenefits from './TopBenefits.tsx';
import SectionHeader from './SectionHeader.tsx';
import { GET_AI_SOLUTION_BY_ID } from '../../graphql/queries/commonQueries.ts';
import { useGraphQLQuery, useGraphQLMutation } from '../../customHooks/useGraphQL.ts';
import { generateDataSource } from '../../services/commonService.tsx';
import { UPDATE_FAVORITE_AI_SOLUTIONS } from '../../graphql/mutations/common.ts';
import { httpConstants } from '../../constants/index.ts';
import { httpService } from '../../utility/httpService.ts';
import { API_LLM_BASE } from '../../config/env.ts';
import { getBenefitClassName, getBenefitLabel, getBenefitTitle } from '../../common/utils.tsx';
import Modal from '../../common/components/Modal/Modal.tsx';
import { Snackbar, Alert } from '@mui/material';
import { getSolutionVideoById } from '../../services/videoService.ts';
import Loader from '../../common/components/Loader.tsx';
// import CustomTooltip from '../../common/components/CustomToolTip/CustomToolTip.tsx';

type ViewModalProps = {
  selectedSolutionId: AISolutionId;
  // onAddToSolutionXPLR?: () => void; // <-- REMOVE if not used
  enterpriseContextApplied?: boolean;
  setEnterpriseContextApplied?: (value: boolean) => void;
};

const ViewModalOld: React.FC<ViewModalProps> = ({
  selectedSolutionId,
  // onAddToSolutionXPLR, // <-- REMOVE if not used
  enterpriseContextApplied = false,
  setEnterpriseContextApplied = () => {},
}) => {
  const [solution, setSolution] = useState<IEnhancedAISolution | null>(null);
  const [source, setSource] = useState<string[]>([]);
  const [challenge, setChallenge] = useState<string[]>([]);
  // const [agentList, setAgentList] = useState<AgentCardProps[]>([]); // <-- REMOVE if not used
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [isApplyingContext, setIsApplyingContext] = useState<boolean>(false);
  const [isLoadingImplementation, setIsLoadingImplementation] = useState<boolean>(false);
  const [data_sources, setDataSources] = useState<string>('');
  const [, setImplementationConsiderations] = useState<string>('');
  const [showApplySuccessSnackbar, setShowApplySuccessSnackbar] = useState(false);

  // --- ADDED for video fix
  // const [videoUrl, setVideoUrl] = useState<string>('');
  // const [videoPoster, setVideoPoster] = useState<string>('');

  // Use refs to track API calls and prevent multiple calls
  const isApplyingContextRef = useRef<boolean>(false);
  const hasAppliedContextRef = useRef<boolean>(false);
  const currentSolutionIdRef = useRef<AISolutionId | null>(null);
  const hasLoadedImplementationRef = useRef<boolean>(false);

  const currentOrg = useMemo(() => localStorage.getItem('current_organization') || 'Empty', []);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);

  // const navigate = useNavigate(); // <-- REMOVE if not used

  const {
    loading: queryLoading,
    error: queryError,
    data: aiSolutionData,
  } = useGraphQLQuery<IAISolutionResponse>(
    GET_AI_SOLUTION_BY_ID,
    {
      v1: {
        id: selectedSolutionId,
      },
    },
    {
      fetchPolicy: 'network-only',
    }
  );

  const { mutate } = useGraphQLMutation<IMutationAISolution>(UPDATE_FAVORITE_AI_SOLUTIONS, {
    onCompleted: (data) => {
      console.log('Favorite updated successfully:', data);
    },
    onError: (error) => {
      console.error('Error updating favorite:', error);
    },
    refetchQueries: ['IMutationAISolution'],
  });

  const getEnterpriseAppliedSolution = useCallback(
    async (payload: EnterpriseApplyPayload): Promise<{ aiSolution: IAISolution }> => {
      return httpService<{ aiSolution: IAISolution }>(
        httpConstants.METHOD_TYPE.POST,
        `${API_LLM_BASE}/api/ai/simulation/apply-landscape/`,
        payload
      );
    },
    []
  );

  // Load implementation data when dataRequirements is not available
  const loadImplementationData = useCallback(
    async (solutionId: AISolutionId) => {
      // FIXED: Check if already loaded for this solution
      if (isLoadingImplementation || hasLoadedImplementationRef.current) {
        console.log('Skipping implementation data load - already loaded or loading');
        return;
      }

      setIsLoadingImplementation(true);
      hasLoadedImplementationRef.current = true; // Mark as loaded immediately

      try {
        const response = await generateDataSource(solutionId);
        if (response) {
          setDataSources(response?.data_sources || '');
          setImplementationConsiderations(response.implementation_considerations || '');
        }
      } catch (error) {
        console.error('Failed to load implementation data:', error);
        hasLoadedImplementationRef.current = false; // Reset on error to allow retry
      } finally {
        setIsLoadingImplementation(false);
      }
    },
    [isLoadingImplementation]
  );

  // Helper function to process solution data - removed from dependencies to prevent recreation
  const processSolutionData = useCallback((rawSolution: IAISolution): IEnhancedAISolution => {
    // Process data requirements
    const allDataRequirements =
      rawSolution.aiOpportunitiesUsed?.flatMap((item) => item.data_requirements || []).join(', ') ||
      '';

    // Process benefits - matching Angular structure
    const allTopBenefits = rawSolution.benefits
      ? Object.entries(rawSolution.benefits).map(([key, value]) => {
          const score = typeof value === 'number' ? value : value?.value || 0;
          const rationale = typeof value === 'number' ? '' : value?.rationale || '';
          return {
            title: getBenefitTitle(key),
            label: getBenefitLabel(score),
            className: getBenefitClassName(score),
            tooltipContent: rationale,
          };
        })
      : [];

    // Process data sources - matching Angular structure
    const fallbackSources = Array.from(
      new Set(
        rawSolution.aiOpportunitiesUsed
          ?.flatMap((item) => item.data_requirements || [])
          .map((req) => req?.trim())
          .filter((req) => !!req)
      )
    );

    // Convert string array to IIDataSource array to maintain type consistency
    const dataSources: IIDataSource[] = rawSolution?.dataSources?.length
      ? rawSolution.dataSources
      : fallbackSources.length > 0
        ? fallbackSources.map((source, index) => ({ id: `fallback_${index}`, name: source }))
        : [];

    // Process key considerations - matching Angular structure
    const keyConsiderations = rawSolution?.keyConsiderations
      ? rawSolution?.keyConsiderations
      : rawSolution.potentialChallenges
          ?.split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0) || [];

    // Process agents
    const allAgents: IAllAgents | undefined = rawSolution.agentCollaboration?.length
      ? {
          description: rawSolution.agentCollaboration.map((c) => c.collaboration).join(' '),
          agents: rawSolution.agentCollaboration
            .flatMap((c) => c.agents)
            .map((agent) => {
              const match = rawSolution.aiOpportunitiesUsed?.find(
                (o) => o.ai_opportunity_id === agent.agent_id
              );
              const isMandatory = match?.must_have === 'MUST HAVE';
              return {
                label: agent.agent_name,
                mustHave: match?.must_have,
                mustHaveLabel: isMandatory ? 'Essential' : 'Optional',
                className: isMandatory ? 'yellow' : 'blue',
              };
            }),
        }
      : undefined;

    // Ensure impactAssessment exists
    const impactAssessment = rawSolution.impactAssessment || {
      general: rawSolution.impactValue || 'Not Available',
    };

    return {
      ...rawSolution,
      allDataRequirements,
      allTopBenefits,
      dataSources, // Now correctly typed as IIDataSource[]
      keyConsiderations,
      allAgents,
      impactAssessment,
    };
  }, []);

  // Handle toggle favorite
  const handleToggleFavorite = useCallback(() => {
    const newFavoriteState = !isFavorite;
    setIsFavorite(newFavoriteState);
    mutate({
      variables: {
        v1: {
          id: selectedSolutionId,
          favorite: newFavoriteState,
        },
      },
    });
    setShowFavoriteModal(false);
  }, [isFavorite, mutate, selectedSolutionId]);

  // Reset context state when solution changes
  useEffect(() => {
    if (currentSolutionIdRef.current !== selectedSolutionId) {
      currentSolutionIdRef.current = selectedSolutionId;
      hasAppliedContextRef.current = false;
      isApplyingContextRef.current = false;
      hasLoadedImplementationRef.current = false; // FIXED: Reset implementation loading state

      // Clear previous solution data
      setDataSources('');
      setImplementationConsiderations('');
      setSource([]);
      setChallenge([]);
    }
  }, [selectedSolutionId]);

  // Apply enterprise context - simplified and protected from multiple calls
  const applyContext = useCallback(async () => {
    // Only prevent multiple simultaneous calls, not re-application
    if (!solution || isApplyingContextRef.current || isApplyingContext) {
      console.log('Skipping context application:', {
        noSolution: !solution,
        isApplyingRef: isApplyingContextRef.current,
        isApplyingState: isApplyingContext,
      });
      return;
    }

    // Set both ref and state to prevent multiple calls
    isApplyingContextRef.current = true;
    setIsApplyingContext(true);

    console.log('Starting enterprise context application');

    try {
      const payload: EnterpriseApplyPayload = {
        aiSolution: solution,
        organization_id: currentOrg,
      };

      const response = await getEnterpriseAppliedSolution(payload);
      const rawSolution = response.aiSolution;

      // Process the enterprise-applied solution
      const enhancedSolution = processSolutionData(rawSolution);

      // Directly update states
      setSolution(enhancedSolution);
      setIsFavorite(rawSolution.favorite || false);

      // Challenges
      const potentialChallenges =
        rawSolution.potentialChallenges
          ?.split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0) || [];
      setChallenge(potentialChallenges);

      const uniqueSources = new Set<string>();
      if (enhancedSolution.dataRequirements && enhancedSolution.dataSources) {
        enhancedSolution.dataSources.forEach((data) => {
          uniqueSources.add(typeof data === 'string' ? data : data.name);
        });
      } else if (enhancedSolution.allDataRequirements) {
        enhancedSolution.allDataRequirements.split(',').forEach((data) => {
          uniqueSources.add(data.trim());
        });
      }
      setSource(Array.from(uniqueSources));

      // FIXED: Only load implementation data if not already loaded and if needed
      if (
        !rawSolution.dataRequirements &&
        !rawSolution.implementationConsiderations &&
        !hasLoadedImplementationRef.current
      ) {
        await loadImplementationData(selectedSolutionId);
      }

      hasAppliedContextRef.current = true;
      console.log('Enterprise context applied successfully');
      setShowApplySuccessSnackbar(true);
    } catch (err) {
      console.error('Error applying enterprise context:', err);
      // Don't reset hasAppliedContextRef on error to allow retry
    } finally {
      isApplyingContextRef.current = false;
      setIsApplyingContext(false);
      setEnterpriseContextApplied(false);
    }
  }, [
    solution,
    currentOrg,
    getEnterpriseAppliedSolution,
    processSolutionData,
    loadImplementationData,
    selectedSolutionId,
    setEnterpriseContextApplied,
    isApplyingContext,
  ]);

  // Initial data loading effect
  useEffect(() => {
    if (queryLoading || !aiSolutionData?.aiSolution?.get) return;

    if (queryError) {
      console.error('Error fetching AI solution data:', queryError);
      return;
    }

    const rawSolution = aiSolutionData.aiSolution.get;
    const enhancedSolution = processSolutionData(rawSolution);

    setSolution(enhancedSolution);
    setIsFavorite(rawSolution.favorite || false);

    // Set potential challenges
    const potentialChallenges =
      rawSolution.potentialChallenges
        ?.split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0) || [];
    setChallenge(potentialChallenges);

    // FIXED: Only load implementation data if not already loaded and if needed
    if (
      !rawSolution.dataRequirements &&
      !rawSolution.implementationConsiderations &&
      !hasLoadedImplementationRef.current
    ) {
      loadImplementationData(selectedSolutionId);
    }
  }, [
    aiSolutionData,
    queryLoading,
    queryError,
    selectedSolutionId,
    processSolutionData,
    loadImplementationData,
  ]);

  useEffect(() => {
    if (!solution?.id) return;

    const loadVideo = async () => {
      const id = String(solution.id);

      try {
        const video = await getSolutionVideoById(id);

        if (video.video_url) {
          // setVideoUrl(video.video_url);
          // setVideoPoster(video.thumbnail_url);
        }
      } catch (err) {
        console.error('❌ Video fetch error:', err);
      }
    };

    loadVideo();
  }, [solution?.id]);

  // Handle enterprise context application - allow re-application when button is clicked
  useEffect(() => {
    if (
      enterpriseContextApplied &&
      !isApplyingContextRef.current &&
      solution &&
      !isApplyingContext
    ) {
      applyContext();
    }
  }, [enterpriseContextApplied, solution, applyContext, isApplyingContext]);

  const solutionData = {
    overview:
      'Automates payment-to-invoice matching using adaptive AI and business rules. Resolves common variances instantly and updates ERP systems in real time to ensure accuracy and efficiency.',
    roi: {
      level: 'High',
    },
    complexity: {
      level: 'High',
    },
    topBenefits: [
      { name: 'Revenue Growth', level: 'MEDIUM', color: '#FFC107' },
      { name: 'Customer Experience', level: 'HIGH', color: '#4CAF50' },
      { name: 'Process Productivity', level: 'HIGH', color: '#4CAF50' },
      { name: 'Employee Productivity', level: 'MEDIUM', color: '#FFC107' },
      { name: 'Cost Savings', level: 'HIGH', color: '#4CAF50' },
    ],
    dataSources: {
      internal: [
        { name: 'Customer Profiles', source: 'Salesforce CRM', type: 'Structured' },
        { name: 'Support Tickets', source: 'ServiceNow Case Management', type: 'Unstructured' },
        { name: 'Knowledge Base Articles', source: 'Salesforce KB', type: 'Structured' },
        {
          name: 'Case Resolution Metrics',
          source: 'Internal Reporting Dashboards',
          type: 'Structured',
        },
      ],
      external: [
        // { name: "Chat Transcripts", source: "Oracle Digital Assistant Conversations", type: "Unstructured" },
        // { name: "Email Threads", source: "Outlook / Gmail Integration", type: "Structured" },
        // { name: "Call Recordings", source: "Contact Center Logs", type: "Unstructured" },
        // { name: "Customer Feedback", source: "Free-text Survey Responses", type: "Unstructured" }
      ],
    },
    keyRequirements: [
      'Accelerates Digital Service Modernization',
      'Drives Scalable Operational Efficiency with Clear ROI',
      'Enhances Customer Loyalty and Trust',
      'Frees Talent for High-Impact Work',
    ],
    enterpriseApplications: [
      // "SAP S/4HANA",
      // "Salesforce Sales Cloud"
    ],
  };

  const renderDataRequirementsContent = useMemo(() => {
    if (solution?.dataSources) {
      const internalSources = solution?.dataSources || [];
      console.log('inter:', internalSources);

      const renderSourceItem = (item: IIDataSource, index: number) => (
        <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <img src="/images/db.svg" alt="" style={{ width: 16, height: 16, padding: 4 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px', mr: 1 }}>
            {typeof item === 'string' ? item : item.name}
          </Typography>
        </Box>
      );

      return (
        <Grid container direction="column">
          {/* Internal Data Sources */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
              Internal Data Sources
            </Typography>
            {internalSources.length > 0 ? (
              internalSources.map(renderSourceItem)
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  fontSize: '14px',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                None
              </Typography>
            )}
          </Box>

          {/* External Data Sources */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
              External Data Sources
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 400, fontSize: '16px', color: 'text.secondary' }}
            >
              None
            </Typography>
          </Box>
        </Grid>
      );
    }

    // Loading State
    if (isLoadingImplementation) {
      return (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(8)].map((_, index) => (
                <Box key={index} sx={{ height: '1.5em', bgcolor: 'grey.200', borderRadius: 1 }} />
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <SectionCard title="Data Sources" titleColor="#2c2c2c">
              <Lists list={[]}>
                <Storage sx={{ mr: 1 }} />
              </Lists>
            </SectionCard>
          </Grid>
        </Grid>
      );
    }

    // Fallback if no data sources
    return (
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography>{data_sources || 'No data requirements available'}</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <SectionCard title="Data Sources" titleColor="#2c2c2c">
            <Lists list={source}>
              <Storage sx={{ mr: 1 }} />
            </Lists>
          </SectionCard>
        </Grid>
      </Grid>
    );
  }, [solution?.dataSources, isLoadingImplementation, data_sources, source]);

  const renderImplementationConsiderationsContent = useMemo(() => {
    const keyConsiderationsList = solution?.keyConsiderations || [];

    if (keyConsiderationsList.length) {
      return (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Lists list={keyConsiderationsList}>
              <img src="/images/Info_implement.svg" />
            </Lists>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Lists list={challenge}>
            <img src="/images/Info_implement.svg" />
          </Lists>
        </Grid>
      </Grid>
    );
  }, [solution?.keyConsiderations, isLoadingImplementation, challenge]);

  const renderEnterpriseApplicationsContent = useMemo(() => {
    const apps: string[] = solutionData?.enterpriseApplications || [];

    if (!apps.length) {
      return (
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          None
        </Typography>
      );
    }

    return apps.map((app, idx) => (
      <Box
        key={idx}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <img
          src="/images/Enterprise.svg"
          alt="Enterprise Application"
          style={{ width: 16, height: 16 }}
        />
        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500 }}>
          {app}
        </Typography>
      </Box>
    ));
  }, [solutionData?.enterpriseApplications]);

  // Show loading state with consistent modal size
  if (isApplyingContext) {
    return (
      <Box
        sx={{
          position: 'relative',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      ></Box>
    );
  }

  // Show error state
  if (queryError) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <Typography color="error">Error loading solution data</Typography>
      </Box>
    );
  }

  const getFirstTwoSentences = (text: string): string => {
    if (!text) return '';

    const sentences = text
      .split('.')
      .map((s) => s.trim())
      .filter(Boolean);
    if (sentences.length === 0) return text;
    if (sentences.length === 1) {
      return sentences[0] + '.';
    }
    return sentences.slice(0, 2).join('. ') + '.';
  };

  return queryLoading ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh',
        width: '100%',
      }}
    >
      <Loader message="Fetching Solution" />
    </Box>
  ) : (
    <Grid container spacing={2} sx={{ mb: 15 }}>
      <Grid size={{ xs: 12, md: 10 }} spacing={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ mb: 3, boxShadow: 'none' }}>
            <CardContent
              sx={{ border: '1px solid #DDE1E6', boxShadow: 'none', borderRadius: '10px' }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1, mr: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Overview
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {getFirstTwoSentences(solution?.description || '')}
                  </Typography>
                  {/* </CustomTooltip> */}
                </Box>
                <Box
                  sx={{
                    background: 'linear-gradient(90deg, #1532B8 24.06%, #1B54F8 100%)',
                    borderRadius: '39px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '161px',
                    height: '33px',
                    opacity: 1,
                    boxShadow: 1,
                    mr: 3,
                  }}
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontFamily: 'Poppins',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '120%',
                      letterSpacing: '-1%',
                      textAlign: 'center',
                      textTransform: 'uppercase',
                    }}
                  >
                    {String(solution?.impactValue)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    lignItems: 'flex-start',
                    minWidth: 320,
                  }}
                >
                  <Box
                    sx={{
                      width: '2px',
                      height: '95px',
                      backgroundColor: '#DDE1E6',
                      borderRadius: '1px',
                      alignSelf: 'stretch',
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        mb: 1,
                        fontSize: '20px',
                        color: '#4D5358',
                        minWidth: 160,
                        marginBottom: '8px',
                      }}
                    >
                      ROI
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '18px',
                      }}
                    >
                      <img
                        src="/images/GreenDot.svg"
                        alt=""
                        style={{ width: 10, height: 10, marginRight: 3 }}
                      />
                      {solutionData.roi.level}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '2px',
                      backgroundColor: '#DDE1E6',
                      borderRadius: '1px',
                      alignSelf: 'stretch',
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        mb: 1,
                        fontSize: '20px',
                        color: '#4D5358',
                        minWidth: 160,
                        marginBottom: '8px',
                      }}
                    >
                      Complexity
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '18px',
                      }}
                    >
                      <img
                        src="/images/GreenDot.svg"
                        alt=""
                        style={{ width: 10, height: 10, marginRight: 3 }}
                      />
                      {solutionData.complexity.level}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Grid
            size={{ xs: 12, md: 12 }}
            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
            spacing={2}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader title="Data Sources" img="/images/data_sources.svg" />
                <Stack
                  spacing={2}
                  alignItems="center"
                  justifyContent="flex-start"
                  sx={{ px: 3, py: 3, border: '1px solid #ccc', alignItems: 'baseline' }}
                >
                  {renderDataRequirementsContent}
                </Stack>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ height: 'max-content', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader title="Key Requirements" img="/images/Info_implement_info.svg" />
                <Stack
                  spacing={2}
                  alignItems="center"
                  justifyContent="flex-start"
                  sx={{
                    flexGrow: 1,
                    px: 3,
                    py: 3,
                    border: '1px solid #ccc',
                    alignItems: 'baseline',
                  }}
                >
                  {renderImplementationConsiderationsContent}
                </Stack>
              </Box>
              {solutionData?.enterpriseApplications.length > 0 && (
                <Box
                  sx={{ height: 'max-content', display: 'flex', flexDirection: 'column', mt: 2 }}
                >
                  <SectionHeader
                    title="Enterpise Application In Use"
                    img="/images/Enterprise1.svg"
                  />
                  <Stack
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-start"
                    sx={{
                      flexGrow: 1,
                      px: 3,
                      py: 3,
                      border: '1px solid #ccc',
                      alignItems: 'baseline',
                    }}
                  >
                    {renderEnterpriseApplicationsContent}
                  </Stack>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <TopBenefits allTopBenefits={solution?.allTopBenefits} />
      </Grid>

      {/* <Grid size={{ xs: 12, md: 4.5 }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SectionHeader title="Data Sources" />
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="flex-start"
            sx={{ px: 3, py: 5, border: '1px solid #ccc' }}
          >
            {renderDataRequirementsContent}
          </Stack>
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 4.5 }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SectionHeader title="Implementation Considerations" />
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="flex-start"
            sx={{ flexGrow: 1, px: 3, py: 5, border: '1px solid #ccc' }}
          >
            {renderImplementationConsiderationsContent}
          </Stack>
        </Box>
      </Grid> */}

      <Grid size={{ xs: 12, md: 12 }}></Grid>

      {/* Action buttons at the bottom */}
      <Modal
        isOpen={showFavoriteModal}
        onClose={() => setShowFavoriteModal(false)}
        title={isFavorite ? 'Remove AI Solution from Favorites' : 'Add AI Solution to Favorites'}
        maxWidth="sm"
        height="md"
      >
        <Box
          sx={{
            bgcolor: '#F9FBFF',
            borderRadius: '12px',
            p: 3,
          }}
        >
          <Stack spacing={2} alignItems="center">
            {/* Icon + Message */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isFavorite ? (
                <Star sx={{ color: '#2054F4' }} />
              ) : (
                <StarBorder sx={{ color: '#2054F4' }} />
              )}
              <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
                Are you sure you want to{' '}
                {isFavorite ? 'remove this AI Solution from' : 'add this AI Solution to'} your
                favorites?
              </Typography>
            </Box>

            {/* Button */}
            <Button
              variant="contained"
              onClick={handleToggleFavorite}
              sx={{
                textTransform: 'none',
                bgcolor: '#2054F4',
                borderRadius: '6px',
                px: 3,
                py: 1,
                '&:hover': { bgcolor: '#1A45C6' },
                fontWeight: 'bold',
              }}
              startIcon={<Add />}
            >
              {isFavorite ? 'Remove' : 'Add'}
            </Button>
          </Stack>
        </Box>
      </Modal>
      <Snackbar
        open={showApplySuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowApplySuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowApplySuccessSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          AI Solution applied successfully
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ViewModalOld;
